// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// Renderer-rule registry for Geodatabase sublayers.
//
// Each rule has a predicate `match` that runs after the GeoJSONLayer loads
// (so we know its true geometryType and fields), and an `apply` that mutates
// the layer to set renderer/elevationInfo/labels. The first matching rule wins.
//
// Rules supersede both styleConfig entries and the 2D default fallback —
// they're the most specific signal because they inspect the actual data.

import type GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';

export interface LayerMeta {
  name: string;
  geometryType: string | null | undefined;
  fields: __esri.Field[];
}

export interface RendererRule {
  id: string;
  match: (meta: LayerMeta) => boolean;
  apply: (layer: GeoJSONLayer) => void;
  legendColor: string;
}

const hasField = (fields: __esri.Field[], name: string): boolean =>
  fields.some((f) => f.name === name);

// Known pole-bottom layer names — used as a fallback when the attribute-based
// detection misses (e.g. when GeoJSONLayer.fields hasn't been inferred yet, or
// when aw-server's GeoJSON conversion changes field casing).
const POLE_BOTTOM_NAMES = new Set(['T_UP_BOTTOM']);

// ─── Symbols ──────────────────────────────────────────────────────────────

// Candidate pole-height field names (feet) on T_UP_BOTTOM, used to extrude the
// cylinder from its base up to the top. Named inconsistently across exports
// (`Height_Ft_`, `Height_ft`, or the DB-renamed `Height (Ft)`); the real-world
// size variable needs a `field` (a valueExpression yields no size on 3D object
// symbols), so the actual name is resolved from the layer schema at apply-time.
const POLE_HEIGHT_FIELDS = ['Height_Ft_', 'Height_ft', 'Height (Ft)'];

const findHeightField = (fields: __esri.Field[]): string | undefined =>
  POLE_HEIGHT_FIELDS.find((name) => hasField(fields, name));

// The utility-pole cylinder, drawn on T_UP_BOTTOM so a click opens the rich
// bottom-layer popup (Diameter_ft, POA_* attachment points, Pole_Image) — matching
// Esri, where the pole is the bottom layer. Its base is anchored to the point's
// absolute geometry Z (see the rule) and the height size variable extrudes it
// Height_ft feet upward, so the top lands at base.Z + Height_ft — i.e. the top
// point's Z, where the cone sits. width/depth are locked to a static 0.5 m
// (~1.6 ft) via useSymbolValue — real diameters (~0.9 ft) are too thin at overview
// zoom, and without the lock a height-axis size variable scales the whole
// primitive into a fat barrel. Plain literals (the cast on use does autocasting).
const buildPoleCylinderRenderer = (heightField: string) => ({
  type: 'simple',
  symbol: {
    type: 'point-3d',
    symbolLayers: [{
      type: 'object',
      resource: { primitive: 'cylinder' },
      // alpha 0.6 so POA points inside the cylinder show through.
      material: { color: [168, 137, 106, 0.6] },
      anchor: 'bottom',
      width: 0.5,
      depth: 0.5,
    }],
  },
  visualVariables: [
    { type: 'size', axis: 'height', field: heightField, valueUnit: 'feet' },
    { type: 'size', axis: 'width-and-depth', useSymbolValue: true },
  ],
});

// Top marker for T_UP_TOP — a red downward cone (inverted-cone, apex anchored at
// the point) sitting on the pole top, matching the Esri Scene Viewer. Anchored to
// the point's absolute geometry Z, which equals the cylinder's base Z + Height_ft
// (when the data is consistent), so its apex lands exactly on the cylinder top.
// Clicking the cone opens the top popup; clicking the cylinder body opens the
// bottom popup.
const POLE_TOP_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'point-3d',
    symbolLayers: [{
      type: 'object',
      resource: { primitive: 'inverted-cone' },
      material: { color: [200, 0, 0] },
      anchor: 'bottom',
      width: 0.9,
      depth: 0.9,
      height: 1.2,
    }],
  },
};

// Pole centerline tube — a 3D path symbol (circular profile) drawn along the
// bottom→top polyline. When the GDB→GeoJSON lambda has both pole layers (paired
// by Pole_ID) it rewrites T_UP_BOTTOM from a point into a bottom→top 3D line, so
// this renders the pole. The line carries per-vertex Z (base→top) and is rendered
// absolute-height, so the tube follows the real 3D vector — it shows the pole's
// lean and its end lands exactly on the top point. It is still the T_UP_BOTTOM
// layer, so a click opens the rich bottom popup. When the lambda can't build the
// line (no Pole_ID / no matching top), T_UP_BOTTOM stays a point and the
// pole-cylinder rule renders a plumb cylinder instead.
const POLE_CENTERLINE_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'line-3d',
    symbolLayers: [{
      type: 'path',
      profile: 'circle',
      // alpha 0.6 makes the pole semi-transparent so attachment points (POA)
      // inside the tube volume show through.
      material: { color: [168, 137, 106, 0.6] },
      width: 0.5,
      height: 0.5,
      cap: 'round',
      join: 'round',
    }],
  },
};

// Marker for an UNPAIRED pole — a T_UP_BOTTOM with no matching T_UP_TOP (e.g. a
// pole captured in the field but not by the point cloud). The lambda splits these
// into a T_UP_BOTTOM_UNPAIRED points layer so one unpaired pole no longer forces
// the whole order back to cylinders. It has no top and often no Height_ft, so it
// can't be drawn as a tube or an accurately-sized cylinder — a sphere at the base
// point marks the location without implying a height, and a click still opens the
// bottom popup (Pole tag + image), which is the whole reason the pole was kept.
const POLE_MARKER_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'point-3d',
    symbolLayers: [{
      type: 'object',
      resource: { primitive: 'sphere' },
      material: { color: [168, 137, 106] },
      width: 1.5,
      depth: 1.5,
      height: 1.5,
    }],
  },
};

const POLE_LABELS: __esri.LabelClassProperties[] = [{
  symbol: {
    type: 'label-3d',
    symbolLayers: [{
      type: 'text',
      material: { color: [255, 255, 255, 1] },
      halo: { color: [0, 0, 0, 1], size: 1 },
      size: 10,
    }],
  },
  labelExpressionInfo: { expression: '$feature.Pole_Tag' },
  labelPlacement: 'center-right',
}];

// WebStyleSymbol — Esri-hosted 3D mesh, resolved at runtime from a published
// style. `EsriRealisticStreetSceneStyleLow` is a public Esri style containing
// hydrants, lamp posts, signs, etc. Browse names at
// https://developers.arcgis.com/javascript/latest/sample-code/symbols-web-style-3d/
const HYDRANT_WEBSTYLE_RENDERER = {
  type: 'simple',
  symbol: {
    type: 'web-style',
    styleName: 'EsriRealisticStreetSceneStyleLow',
    name: 'Fire_Hydrant_1',
  },
};

// Popups for GDB features are rendered by the custom React PopupWindow
// component in ../PopupWindow.tsx (mounted from ../index.tsx via the click
// hit-test handler). Don't set layer.popupTemplate here — doing so causes
// Esri's native popup to appear alongside the custom one.

// ─── Rules ────────────────────────────────────────────────────────────────

export const RENDERER_RULES: RendererRule[] = [
  {
    // Unpaired pole marker (T_UP_BOTTOM_UNPAIRED) — a bottom with no matching top,
    // split into its own points layer by the lambda. Rendered as a sphere at the
    // base (no top to lean toward, often no Height_ft to extrude); a click opens
    // the rich bottom popup (Pole tag + image). Listed first so the exact name
    // wins before the pole-cylinder rule's fuzzy Diameter_ft fallback.
    id: 'pole-unpaired-marker',
    legendColor: '#A8896A',
    match: ({ name, geometryType }) =>
      geometryType === 'point' && name === 'T_UP_BOTTOM_UNPAIRED',
    apply: (layer) => {
      layer.renderer = POLE_MARKER_RENDERER as unknown as __esri.Renderer;
      layer.elevationInfo = { mode: 'absolute-height' };
      if (layer.fields && hasField(layer.fields, 'Pole_Tag')) {
        layer.labelingInfo = POLE_LABELS as unknown as __esri.LabelClass[];
        layer.labelsVisible = true;
      }
    },
  },
  {
    // Pole CYLINDER on the base layer (T_UP_BOTTOM) — the selectable pole body;
    // clicking it opens the rich bottom-layer popup (Diameter_ft, POA_* attachment
    // points, Pole_Image), matching Esri. on-the-ground + Height_Ft_ size variable.
    // Matches the known base name or any point layer carrying Diameter_ft (catches
    // future base exports). Name fallback covers empty/case-normalized fields at
    // .when() time.
    id: 'pole-cylinder',
    legendColor: '#A8896A',
    match: ({ name, geometryType, fields }) => {
      if (geometryType !== 'point') return false;
      if (POLE_BOTTOM_NAMES.has(name)) return true;
      return !!findHeightField(fields) && hasField(fields, 'Diameter_ft');
    },
    apply: (layer) => {
      const heightField = findHeightField(layer.fields ?? []);
      if (heightField) {
        layer.renderer = buildPoleCylinderRenderer(heightField) as unknown as __esri.Renderer;
      }
      // absolute-height anchored to the point's geometry Z (the base, already in
      // metres from the pipeline); the size variable extrudes it Height_ft feet
      // upward, so the top reaches base.Z + Height_ft = the top point's Z, where
      // the cone sits. Positioning off geometry Z (not the Elevation attribute)
      // keeps the cylinder, the leaning-tube line, and the cone on one vertical
      // reference — the Elevation attribute can carry a geoid/datum offset the
      // geometry Z does not. Matches the Esri Scene Viewer, which uses point Z.
      layer.elevationInfo = { mode: 'absolute-height' };
      if (layer.fields && hasField(layer.fields, 'Pole_Tag')) {
        layer.labelingInfo = POLE_LABELS as unknown as __esri.LabelClass[];
        layer.labelsVisible = true;
      }
    },
  },
  {
    // Pole-top marker (T_UP_TOP): red downward cone anchored to the point's
    // absolute geometry Z, so its apex caps the pole (= base.Z + Height_ft when
    // the data is consistent). Name-matched to T_UP_TOP (no Diameter_ft, so it
    // never matches the pole-cylinder rule).
    id: 'pole-top',
    legendColor: '#C80000',
    match: ({ name, geometryType }) =>
      geometryType === 'point' && name === 'T_UP_TOP',
    apply: (layer) => {
      layer.renderer = POLE_TOP_RENDERER as unknown as __esri.Renderer;
      // Anchored to the point's geometry Z — the same vertical reference the
      // cylinder base and the leaning-tube line use — so the cone apex lands
      // exactly on the pole top regardless of any geoid/datum offset baked into
      // the Elevation attribute (which the geometry Z does not carry).
      layer.elevationInfo = { mode: 'absolute-height' };
    },
  },
  {
    // Pole as a LEANING TUBE — T_UP_BOTTOM after the lambda has rewritten it from a
    // point into a bottom→top 3D line (paired by Pole_ID). absolute-height uses the
    // line's per-vertex Z, so it shows the real lean and ends exactly on the top
    // point. Still the T_UP_BOTTOM layer, so a click opens the rich bottom popup.
    id: 'pole-line',
    legendColor: '#A8896A',
    match: ({ name, geometryType }) =>
      geometryType === 'polyline' && POLE_BOTTOM_NAMES.has(name),
    apply: (layer) => {
      layer.renderer = POLE_CENTERLINE_RENDERER as unknown as __esri.Renderer;
      layer.elevationInfo = { mode: 'absolute-height' };
      if (layer.fields && hasField(layer.fields, 'Pole_Tag')) {
        layer.labelingInfo = POLE_LABELS as unknown as __esri.LabelClass[];
        layer.labelsVisible = true;
      }
    },
  },
  {
    // Fire hydrants: render as the Esri Realistic Street Scene hydrant mesh.
    // Pure name match — there's no distinguishing attribute schema.
    id: 'hydrant-webstyle',
    legendColor: '#FF0000',
    match: ({ name, geometryType }) =>
      geometryType === 'point' && name === 'U_HYDRANT',
    apply: (layer) => {
      layer.renderer = HYDRANT_WEBSTYLE_RENDERER as unknown as __esri.Renderer;
      layer.elevationInfo = { mode: 'on-the-ground' };
    },
  },
];

export const findRule = (meta: LayerMeta): RendererRule | undefined =>
  RENDERER_RULES.find((r) => r.match(meta));
